import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Button, Chip, CircularProgress, TextField } from '@mui/material';
import { X } from 'lucide-react';
import {
  findRedundancySet,
  redundancySetConflictOf,
  usePutRedundancySetTags,
  useRedundancySets,
  useRemoveRedundancySetMember,
  useTagSuggestions,
} from '../hooks/useRedundancySets';
import type { RedundancySet, RedundancySetConflict, TagSuggestionKind } from '../types';
import { normalizeAssetTag } from '../utils/normalizeAssetTag';
import { MINIMUM_QUERY_LENGTH, SUGGESTION_DEBOUNCE_MS } from '../utils/tagSuggestionLookup';
import { AppIcon } from './LucideIcon';

const SAVED_NOTICE_MS = 3000;

interface PickerOption {
  /** An existing set to join, or a tag in no set to start a set with. */
  source: 'set' | 'tag';
  key: string;
  label: string;
  /** The tag sent beside the open item's: one member of the set, or the free tag. */
  tag: string;
}

/** What the last failed write answered. */
interface WriteFailure {
  message: string;
  conflict: RedundancySetConflict | null;
}

export interface RedundancySetSectionProps {
  /** The open drawing, which the tag suggestions are asked through. */
  docId: string;
  domainId: string;
  /** The item's tag as it is stored, not as it is being typed. Empty disables the section. */
  savedTag: string;
  /** Which family the picker's tag suggestions come from. */
  kind: TagSuggestionKind;
  readOnly?: boolean;
}

function writeFailureOf(error: unknown): WriteFailure {
  const conflict = redundancySetConflictOf(error);
  if (conflict) return { message: conflict.message, conflict };
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return {
    message: typeof detail === 'string' ? detail : 'The redundancy set was not saved.',
    conflict: null,
  };
}

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * The redundancy set of one tagged equipment or instrument: its members, and one
 * picker that adds a member.
 *
 * DECISION(2026-09-17, abhishek): Adding or removing a member saves immediately and
 * does not wait for the dialog's Save. The saving, saved and failed states are the
 * section's signal of it.
 * The sets live in their own store with their own write, so staging the change
 * behind Save would make one button commit to two stores and leave a half-saved
 * state when one fails. A wrong outcome shows as an SME pressing Cancel and
 * expecting the set change to be undone.
 *
 * DECISION(2026-09-17, abhishek): One autocomplete adds a member. Its flat option
 * list holds the plant's existing sets first, each labelled by its member tags, and
 * then the tags that are in no set. Picking a set joins it; picking a tag starts a
 * new set with it.
 * A list of existing sets alone cannot create the first set for two tags that sit
 * on different drawings.
 */
const RedundancySetSection: React.FC<RedundancySetSectionProps> = ({
  docId,
  domainId,
  savedTag,
  kind,
  readOnly = false,
}) => {
  const ownTag = normalizeAssetTag(savedTag);
  const hasSavedTag = Boolean(ownTag);

  const setsQuery = useRedundancySets(hasSavedTag ? domainId : '');
  const putTags = usePutRedundancySetTags(domainId);
  const removeMember = useRemoveRedundancySetMember(domainId);

  const [typedText, setTypedText] = useState('');
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  const [savedNoticeShown, setSavedNoticeShown] = useState(false);

  const sets = setsQuery.data;
  const ownSet = findRedundancySet(sets, ownTag);
  const isSaving = putTags.isPending || removeMember.isPending;
  const controlsDisabled = readOnly || !hasSavedTag || isSaving || !setsQuery.isSuccess;

  const suggestionQuery = useDebouncedValue(typedText.trim(), SUGGESTION_DEBOUNCE_MS);
  const suggestions = useTagSuggestions(
    docId,
    kind,
    suggestionQuery,
    !controlsDisabled && suggestionQuery.length >= MINIMUM_QUERY_LENGTH[kind],
  );

  useEffect(() => {
    if (!savedNoticeShown) return undefined;
    const timer = window.setTimeout(() => setSavedNoticeShown(false), SAVED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [savedNoticeShown]);

  const options = useMemo<PickerOption[]>(() => {
    // An item already in a set joins no other: the server answers 409 for a
    // request that spans two sets.
    const joinableSets: RedundancySet[] = ownSet ? [] : sets ?? [];
    const setOptions = joinableSets.map<PickerOption>((set) => ({
      source: 'set',
      key: `set:${set.id}`,
      label: set.tags.join(', '),
      tag: set.tags[0],
    }));
    const tagOptions = (suggestions.data ?? [])
      .filter((suggestion) => {
        const normalizedTag = normalizeAssetTag(suggestion.tag);
        return normalizedTag !== ownTag && !findRedundancySet(sets, normalizedTag);
      })
      .map<PickerOption>((suggestion) => ({
        source: 'tag',
        key: `tag:${suggestion.tag}`,
        label: suggestion.tag,
        tag: suggestion.tag,
      }));
    return [...setOptions, ...tagOptions];
  }, [ownSet, ownTag, sets, suggestions.data]);

  const beginWrite = () => {
    setFailure(null);
    setSavedNoticeShown(false);
  };

  const handlePick = (option: PickerOption) => {
    beginWrite();
    putTags.mutate([savedTag, option.tag], {
      onSuccess: () => {
        setTypedText('');
        setSavedNoticeShown(true);
      },
      onError: (error) => setFailure(writeFailureOf(error)),
    });
  };

  const handleRemove = (tag: string) => {
    beginWrite();
    removeMember.mutate(tag, {
      onSuccess: () => setSavedNoticeShown(true),
      onError: (error) => setFailure(writeFailureOf(error)),
    });
  };

  const memberTags = ownSet ? [ownTag, ...ownSet.tags.filter((tag) => tag !== ownTag)] : [];

  return (
    <section
      aria-label="Redundancy set"
      style={{
        padding: 10,
        border: '1px solid var(--raven-divider)',
        borderRadius: 6,
        background: 'var(--raven-surface-tier1)',
        fontSize: 12,
        color: hasSavedTag ? 'var(--raven-text-primary)' : 'var(--raven-text-disabled)',
      }}
    >
      <div style={{ fontWeight: 600 }}>Redundancy set</div>
      <div style={{ marginTop: 2, color: 'var(--raven-text-secondary)', fontSize: 11 }}>
        A redundancy set groups identical equipment in the same service, for example P-601A
        and P-601B.
      </div>
      <div style={{ marginTop: 2, color: 'var(--raven-text-secondary)', fontSize: 11 }}>
        At any time one unit runs as the primary and the others are its spares, and the roles
        can swap.
      </div>

      {!hasSavedTag && (
        <div style={{ marginTop: 8 }}>
          Save a tag first to add this item to a redundancy set.
        </div>
      )}

      {hasSavedTag && setsQuery.isPending && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--raven-text-secondary)' }}>
          <CircularProgress size={12} /> Loading redundancy sets…
        </div>
      )}

      {hasSavedTag && setsQuery.isError && (
        <Alert
          severity="error"
          sx={{ mt: 1, fontSize: 12 }}
          action={<Button size="small" onClick={() => void setsQuery.refetch()}>Retry</Button>}
        >
          Could not load the redundancy sets.
        </Alert>
      )}

      {memberTags.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {memberTags.map((tag) => {
            const isOwnTag = tag === ownTag;
            return (
              <Chip
                key={tag}
                size="small"
                color={isOwnTag ? 'primary' : 'default'}
                variant={isOwnTag ? 'filled' : 'outlined'}
                label={tag}
                disabled={controlsDisabled}
                onDelete={() => handleRemove(tag)}
                deleteIcon={
                  <span
                    role="button"
                    aria-label={isOwnTag ? `Leave the set as ${tag}` : `Remove ${tag} from the set`}
                    style={{ display: 'inline-flex' }}
                  >
                    <AppIcon icon={X} size={14} />
                  </span>
                }
              />
            );
          })}
        </div>
      )}

      {hasSavedTag && (
        <Autocomplete<PickerOption, false, false, false>
          sx={{ mt: 1.5 }}
          size="small"
          options={options}
          value={null}
          inputValue={typedText}
          // Only the SME's own typing moves the text. MUI also reports the resets it
          // makes on select and on blur, and the blur one arrives during its render.
          onInputChange={(_event, text, reason) => {
            if (reason === 'input' || reason === 'clear') setTypedText(text);
          }}
          onChange={(_event, option) => {
            if (option) handlePick(option);
          }}
          getOptionLabel={(option) => option.label}
          getOptionKey={(option) => option.key}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          // Suggested tags arrive already matched to the typed text; a set is
          // matched here, against the tags that label it.
          filterOptions={(candidates, state) => {
            const typed = state.inputValue.trim().toLowerCase();
            return candidates.filter(
              (option) => option.source === 'tag' || option.label.toLowerCase().includes(typed),
            );
          }}
          renderOption={({ key: _key, ...optionProps }, option) => (
            <li key={option.key} {...optionProps} style={{ fontSize: 12 }}>
              {option.label}
            </li>
          )}
          loading={suggestions.isFetching}
          disabled={controlsDisabled}
          blurOnSelect
          noOptionsText="No set or free tag matches"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add a primary or spare unit"
              placeholder="Type a tag, for example P-601B"
              // The label stays raised so the placeholder shows before the field has focus.
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              inputProps={{ ...params.inputProps, style: { fontSize: 12 } }}
            />
          )}
        />
      )}

      <div role="status" style={{ marginTop: 6, minHeight: 16, color: 'var(--raven-text-secondary)', fontSize: 11 }}>
        {isSaving && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CircularProgress size={10} /> Saving…
          </span>
        )}
        {!isSaving && savedNoticeShown && <span style={{ color: 'var(--raven-success)' }}>Saved</span>}
      </div>

      {failure?.conflict && (
        <Alert severity="warning" sx={{ mt: 0.5, fontSize: 12 }}>
          <div>{failure.message}</div>
          <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
            {failure.conflict.sets.map((set) => (
              <li key={set.id}>{set.tags.join(', ')}</li>
            ))}
          </ul>
          <div>Remove a member from one set first.</div>
        </Alert>
      )}
      {failure && !failure.conflict && (
        <Alert severity="error" sx={{ mt: 0.5, fontSize: 12 }}>
          {failure.message}
        </Alert>
      )}
    </section>
  );
};

export default RedundancySetSection;
